import { useEffect, useState } from 'react';
import { useAdminUsers } from '@/hooks/useAdminUsers';
import { UserCard } from '@/components/admin/users/UserCard';
import { UserFilters } from '@/components/admin/users/UserFilters';
import type { AdminUser } from '@/integrations/supabase/user-types';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Users } from 'lucide-react';

export default function AdminUsers() {
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    country: 'all',
    showDeleted: false
  });

  const { users, isLoading, error } = useAdminUsers(filters.status === 'deleted');
  const [filteredUsers, setFilteredUsers] = useState<AdminUser[]>([]);

  useEffect(() => {
    if (!users) return;

    let filtered = [...users];

    // Filtrar por búsqueda
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(user => 
        user.email?.toLowerCase().includes(searchLower) ||
        user.tutor_name?.toLowerCase().includes(searchLower) ||
        user.student_name?.toLowerCase().includes(searchLower) ||
        user.whatsapp?.toLowerCase().includes(searchLower)
      );
    }

    // Filtrar por estado
    if (filters.status !== 'all') {
      filtered = filtered.filter(user => {
        switch (filters.status) {
          case 'unconfirmed':
            return !user.email_confirmed;
          case 'trial':
            return user.trial_active;
          case 'grace_period':
            return user.in_grace_period;
          case 'premium':
            return user.is_premium;
          case 'expired':
            return user.estado === 'Trial expirado';
          case 'deleted':
            return user.estado === 'Eliminado';
          default:
            return true;
        }
      });
    }

    // Filtrar por país
    if (filters.country !== 'all') {
      filtered = filtered.filter(user => user.country_code === filters.country);
    }

    setFilteredUsers(filtered);
  }, [users, filters]);

  if (isLoading) return <div>Cargando usuarios...</div>;
  if (error) return <div>Error al cargar usuarios: {error.message}</div>;
  if (!users) return null;

  return (
    <AdminLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center mb-8">
          <div className="flex items-center gap-3">
            <Users className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-bold text-gray-900">Administración de Usuarios</h1>
          </div>
        </div>

        <UserFilters 
          filters={filters}
          onChange={setFilters}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredUsers.map(user => (
            <UserCard key={user.id} user={user} />
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}